import { matchesAdapterAnswers } from "./answers";
import type {
  AdapterApplyAnswers,
  AdapterDefinition,
  AdapterMappingDefinition,
  AdapterOnboardingChecklistItem,
  AdapterStepBlock,
  AdapterStepBlockKind,
  AdapterStepBlockOfKind,
  AdapterWorkflowStepDefinition,
  AdapterResourceDeclaration,
  AdapterResourceKind,
} from "./types";

/** A step's blocks of one kind. */
export const getStepBlocksOfKind = <TKind extends AdapterStepBlockKind>(
  step: Pick<AdapterWorkflowStepDefinition, "blocks">,
  kind: TKind,
): AdapterStepBlockOfKind<TKind>[] =>
  (step.blocks ?? []).filter((block): block is AdapterStepBlockOfKind<TKind> => block.kind === kind);

export const hasStepBlock = (step: Pick<AdapterWorkflowStepDefinition, "blocks">, kind: AdapterStepBlockKind): boolean =>
  (step.blocks ?? []).some((block) => block.kind === kind);

/** Reads block-owned data (questions, checklist items) from every step's blocks of that kind. */
export const getWorkflowBlocksOfKind = <TKind extends AdapterStepBlockKind>(
  definition: AdapterDefinition,
  kind: TKind,
): { step: AdapterWorkflowStepDefinition; block: AdapterStepBlockOfKind<TKind> }[] =>
  definition.workflow.flatMap((step) => getStepBlocksOfKind(step, kind).map((block) => ({ step, block })));

/** Every resource the step's blocks declare, in block order. */
export const getStepResourceDeclarations = (step: Pick<AdapterWorkflowStepDefinition, "blocks">): AdapterResourceDeclaration[] =>
  step.blocks.flatMap((block): AdapterResourceDeclaration[] => ("resources" in block ? block.resources : []));

export const getStepResources = <TKind extends AdapterResourceKind>(
  step: AdapterWorkflowStepDefinition,
  kind: TKind,
): Extract<AdapterResourceDeclaration, { kind: TKind }>[] =>
  getStepResourceDeclarations(step).filter(
    (resource): resource is Extract<AdapterResourceDeclaration, { kind: TKind }> => resource.kind === kind,
  );

export const getWorkflowStep = (
  definition: AdapterDefinition,
  stepId: string | undefined,
): AdapterWorkflowStepDefinition | undefined => definition.workflow.find((step) => step.key === stepId);

export interface AdapterWorkflowResource<TKind extends AdapterResourceKind = AdapterResourceKind> {
  step: AdapterWorkflowStepDefinition;
  declaration: Extract<AdapterResourceDeclaration, { kind: TKind }>;
}

/** Every resource declared anywhere in the workflow, optionally restricted to the given resource kinds. */
export const getWorkflowResources = <TKind extends AdapterResourceKind>(
  definition: AdapterDefinition,
  kinds: readonly TKind[],
): AdapterWorkflowResource<TKind>[] =>
  definition.workflow.flatMap((step) =>
    getStepResourceDeclarations(step)
      .filter((declaration): declaration is Extract<AdapterResourceDeclaration, { kind: TKind }> =>
        (kinds as readonly string[]).includes(declaration.kind))
      .map((declaration) => ({ step, declaration })),
  );

export const MAPPING_RESOURCE_KINDS = ["mapping", "deltaMapping"] as const;

/** Resolves an adapter-local logical resource key to the workflow step that declares it. */
export const getResourceStepId = (
  definition: AdapterDefinition,
  kinds: readonly AdapterResourceKind[],
  resourceKey: string,
): string | undefined =>
  getWorkflowResources(definition, kinds).find(({ declaration }) => declaration.resource.key === resourceKey)?.step.key;

export const getStepMapping = (step: AdapterWorkflowStepDefinition): AdapterMappingDefinition | undefined =>
  getStepResourceDeclarations(step).flatMap((declaration) =>
    declaration.kind === "mapping" || declaration.kind === "deltaMapping" ? [declaration.resource] : [])[0];

export const isStepApplicable = (step: AdapterWorkflowStepDefinition, answers: AdapterApplyAnswers): boolean =>
  matchesAdapterAnswers(answers, step.showWhen);

/** Whether a step's resources ship a field guide the reviewer or user can open (delta mappings have none). */
export const stepHasGuide = (step: AdapterWorkflowStepDefinition): boolean =>
  getStepResourceDeclarations(step).some((declaration) =>
    declaration.kind !== "deltaMapping" &&
    "guidance" in declaration.resource && Boolean(declaration.resource.guidance?.length));

/** Confirmation key for an external config block's optional "Done" checkbox. */
export const getExternalConfigConfirmationId = (stepKey: string, blockId: string): string =>
  `externalConfig:${stepKey}:${blockId}`;

/** The step's blocks that apply to the current answers. */
export const getApplicableStepBlocks = (
  step: Pick<AdapterWorkflowStepDefinition, "blocks">,
  answers: AdapterApplyAnswers,
): AdapterStepBlock[] => (step.blocks ?? []).filter((block) => matchesAdapterAnswers(answers, block.showWhen));

/**
 * The blocks a step shows for the current answers, in order. A step without a content block starts
 * with `defaultContent` (its main block's introduction).
 */
export const getStepBlocks = (
  step: Pick<AdapterWorkflowStepDefinition, "blocks">,
  answers: AdapterApplyAnswers,
  defaultContent?: string,
): AdapterStepBlock[] => {
  const blocks = getApplicableStepBlocks(step, answers);
  return hasStepContent(step) || !defaultContent ? blocks : [{ kind: "content", content: defaultContent }, ...blocks];
};

/** Whether the step declares its own introduction content. */
export const hasStepContent = (step: Pick<AdapterWorkflowStepDefinition, "blocks">): boolean =>
  (step.blocks ?? []).some((block) => block.kind === "content");

/** Every checklist item the user must confirm, from connector, verification and finish blocks. */
export const getVerificationItems = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): AdapterOnboardingChecklistItem[] => {
  const applicable = <TKind extends AdapterStepBlockKind>(kind: TKind) =>
    getWorkflowBlocksOfKind(definition, kind)
      .filter(({ step, block }) => isStepApplicable(step, answers) && matchesAdapterAnswers(answers, block.showWhen))
      .map(({ block }) => block);
  return [
    ...applicable("connector").flatMap((block) => block.items ?? []),
    ...applicable("verification").flatMap((block) => block.items),
    ...applicable("finish").flatMap((block) => block.verificationItems ?? []),
  ].filter((item) => matchesAdapterAnswers(answers, item.showWhen));
};
